import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link, type To } from 'react-router-dom';
import { ArrowLeft, Loader2, Printer } from 'lucide-react';
import { Previewer } from 'pagedjs';

import './printChrome.css';
import { pagedPrintStylesheet } from '@/lib/print/pagedPrintStylesheet';
import {
  mergePrintPageMargins,
  type PrintPageMarginsMm,
} from '@/lib/print/paperPresets';

export interface PrintChromeProps {
  documentTitle: string;
  /** CSS `@page` size token only (e.g. `letter`, `A4`); portrait is assumed */
  pageSizeCss: string;
  /** Overrides default `@page` margins (mm). */
  pageMarginsMm?: Partial<PrintPageMarginsMm>;
  /** Screen preview approximate width */
  previewMaxWidthMm: number;
  backHref?: To;
  backLabel?: string;
  /** Passed to react-router `<Link state>` when `backHref` is set */
  backState?: unknown;
  toolbarAside?: ReactNode;
  /** Page content (printed) */
  children: ReactNode;
  /**
   * Change this when printable content changes so Paged.js can re-chunk
   * (e.g. item updatedAt + field visibility, or roadmap reload).
   */
  pagedRenderKey?: string;
  /** Set false to skip Paged.js (plain flow; still uses light-theme print CSS). */
  usePagedJs?: boolean;
}

/**
 * Print shell: optional Paged.js pagination + `@page` sizing, light theme for ink-friendly output.
 */
export function PrintChrome({
  documentTitle,
  pageSizeCss,
  pageMarginsMm,
  previewMaxWidthMm,
  backHref,
  backState,
  backLabel = 'Back',
  toolbarAside,
  children,
  pagedRenderKey = '',
  usePagedJs = true,
}: PrintChromeProps) {
  const sourceRef = useRef<HTMLDivElement>(null);
  const outputRef = useRef<HTMLDivElement>(null);
  const previewerRef = useRef<Previewer | null>(null);

  const [pagedError, setPagedError] = useState(false);
  const [pagedBusy, setPagedBusy] = useState(false);

  /** `@page` only when not using Paged (Paged injects rules via polisher) or after Paged failed. */
  useLayoutEffect(() => {
    if (usePagedJs && !pagedError) return;

    const m = mergePrintPageMargins(pageMarginsMm);
    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-priorities-dynamic-print-page', '');
    styleEl.textContent = `@media print {
  html, body { margin: 0 !important; padding: 0 !important; }
  @page {
    size: ${pageSizeCss} portrait;
    margin-top: ${m.top}mm;
    margin-right: ${m.right}mm;
    margin-bottom: ${m.bottom}mm;
    margin-left: ${m.left}mm;
  }
}`;
    document.head.appendChild(styleEl);
    return () => {
      styleEl.remove();
    };
  }, [usePagedJs, pagedError, pageSizeCss, pageMarginsMm]);

  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains('dark');
    const prevScheme = root.style.colorScheme;
    root.style.colorScheme = 'light';
    if (hadDark) root.classList.remove('dark');
    return () => {
      if (prevScheme) root.style.colorScheme = prevScheme;
      else root.style.removeProperty('color-scheme');
      if (hadDark) root.classList.add('dark');
    };
  }, []);

  useEffect(() => {
    const prev = document.title;
    document.title = documentTitle;
    return () => {
      document.title = prev;
    };
  }, [documentTitle]);

  useEffect(() => {
    setPagedError(false);
  }, [pageSizeCss, pageMarginsMm, pagedRenderKey]);

  useEffect(() => {
    if (!usePagedJs || pagedError) return undefined;

    const source = sourceRef.current;
    const output = outputRef.current;
    if (!source || !output) return undefined;

    let cancelled = false;

    async function run() {
      setPagedBusy(true);
      setPagedError(false);

      try {
        previewerRef.current?.polisher.destroy();
        previewerRef.current = null;

        output.innerHTML = '';

        document.querySelectorAll('style[data-pagedjs-inserted-styles]').forEach((el) => el.remove());

        const previewer = new Previewer();
        previewerRef.current = previewer;

        const sheets = [pagedPrintStylesheet(pageSizeCss, mergePrintPageMargins(pageMarginsMm))];

        await previewer.preview(source, sheets, output);

        if (cancelled) {
          previewer.polisher.destroy();
          previewerRef.current = null;
          return;
        }
      } catch (e) {
        console.error('[PrintChrome] Paged.js layout failed', e);
        if (!cancelled) setPagedError(true);
      } finally {
        if (!cancelled) setPagedBusy(false);
      }
    }

    void run();

    return () => {
      cancelled = true;
      previewerRef.current?.polisher.destroy();
      previewerRef.current = null;
      if (outputRef.current) outputRef.current.innerHTML = '';
      document.querySelectorAll('style[data-pagedjs-inserted-styles]').forEach((el) => el.remove());
    };
  }, [usePagedJs, pagedError, pagedRenderKey, pageSizeCss, pageMarginsMm]);

  const showPaged = usePagedJs && !pagedError;
  /*
   * Full paper-width flow so roadmap tables use the printable width; @page handles sheet inset.
   * Light inner padding on screen only — avoids fighting @page when printing.
   */
  const innerClass =
    'priorities-print-inner max-w-none w-full text-[13px] leading-relaxed text-slate-900 bg-white px-8 py-10 print:p-0 print:max-w-none';

  return (
    <div className="priorities-print-chrome min-h-screen bg-slate-200 text-slate-900 print:bg-white print:text-black">
      <header className="print:hidden sticky top-0 z-20 border-b border-slate-300 bg-white px-4 py-3 shadow-sm flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-3 w-full">
          {backHref ? (
            <Link
              to={backHref}
              state={backState}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              {backLabel}
            </Link>
          ) : null}
          <div className="flex flex-1 flex-wrap items-center justify-end gap-2 min-w-[12rem]">
            {toolbarAside}
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 shadow-sm transition-colors"
            >
              <Printer className="h-4 w-4" aria-hidden />
              Print
            </button>
          </div>
        </div>
        <p className="text-[11px] text-slate-500 leading-snug max-w-[52rem]">
          Browsers add the URL and date in the footer unless you turn them off: in Chrome print, open
          &quot;More settings&quot; and disable &quot;Headers and footers&quot; (Safari: uncheck headers/footers in
          print options). CSS cannot remove those.
        </p>
      </header>

      {showPaged ? (
        <>
          <div
            ref={sourceRef}
            className="priorities-print-paged-source print:hidden pointer-events-none fixed left-[-10000px] top-0 z-0 overflow-hidden bg-white"
            style={{ width: `${previewMaxWidthMm}mm` }}
            aria-hidden
          >
            <div className={innerClass}>{children}</div>
          </div>

          <div className="relative mx-auto my-6 w-full max-w-full px-2 print:m-0 print:p-0">
            <div
              ref={outputRef}
              className="priorities-pagedjs-root min-h-[120px] print:min-h-0"
              aria-busy={pagedBusy}
            />
            {pagedBusy ? (
              <div className="print:hidden pointer-events-none absolute inset-0 flex items-start justify-center pt-12 text-sm text-slate-500 gap-2">
                <Loader2 className="h-5 w-5 animate-spin shrink-0" aria-hidden />
                Laying out pages…
              </div>
            ) : null}
          </div>
        </>
      ) : (
        <main
          className="priorities-print-sheet mx-auto my-6 rounded-lg border border-slate-300 bg-white px-8 py-10 shadow-lg print:m-0 print:rounded-none print:border-0 print:shadow-none print:p-0"
          style={{ maxWidth: `${previewMaxWidthMm}mm`, width: '100%' }}
        >
          {children}
        </main>
      )}
    </div>
  );
}
